import { Tab, Tabs } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { ELEMENT_SCOPE, ELEMENT_TYPE, PROPERTY_TIERS, TAB_STATUSES } from "@/constants";
import { useAccessControl, useUrlParams } from "@/hooks";
import { MapElementIcon, ShareCopyIcon, VoiceIcon } from "@/icons";
import { ContentElementService, ElementService } from "@/services";
import { resetCurrentElement } from "@/store/actions";
import { currentElementSelector, currentExperienceSelector, flagsSelector } from "@/store/selectors";
import { deleteContentElement, deleteElement } from "@/utils/element";

import LinkPanel from "../LinkPanel/LinkPanel";
import MapPanel from "../MapPanel/MapPanel";
import VoiceMemoPanel from "../VoiceMemo/VoiceMemoPanel/VoiceMemoPanel";

const useStyles = makeStyles({
  topTabSection: {
    display: "flex",
    flexDirection: "column",
    zIndex: 1,
    width: "100%",
    height: "100%",
    color: "#fff",

    "& .MuiTabs-root": {
      width: "100%",
      gap: 8,
    },
    "& .MuiTabs-flexContainer": {
      gap: "100px",
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
      borderBottom: "1px solid #757575",
      padding: "0 108px",
    },
  },
  icon: {
    fontSize: "1.5rem",
    fill: "none",
  },
  map: {
    fontSize: "1.1rem",
  },
  voiceMemo: {
    fontSize: "1rem",
  },
  link: {
    fontSize: "1.5rem",
  },
  tabsRoot: {
    marginBottom: 32,
  },
  tab: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    maxWidth: "264px",
    minWidth: "unset",
    fontSize: "14px",
    padding: 0,
    lineHeight: "20px",
    height: "63px",
    minHeight: "63px",
    fontWeight: 400,
    color: "#FFF",
    borderBottom: "3px solid #000",

    "&:focus-visible": {
      border: "1px solid white",
    },
    "& > .MuiTab-iconWrapper": {
      marginRight: "0px",
    },
    "&.MuiButtonBase-root.MuiTab-root": {
      color: "#fff",
      flexDirection: "column",
      gap: 6,
    },
    "&.MuiButtonBase-root.MuiTab-root.Mui-selected": {
      borderBottom: "3px solid #F50561",
    },
  },
});

const ELEMENT_TAB_NAME = {
  LINK: "Link",
  MAP: "Map",
  VOICE_MEMO: "Voice Memo",
};

const ElementsTabs = () => {
  const classes = useStyles();
  const params = useUrlParams();
  const dispatch = useDispatch();
  const { canAccess } = useAccessControl();
  const { propertyId, experienceId, tabStatus } = params;
  const { element } = useSelector(currentElementSelector);
  const { flags } = useSelector(flagsSelector);
  const tierAccessFlag = flags["tiers-phase-three"];
  const [selectedTab, setSelectedTab] = useState(element.type || ELEMENT_TYPE.LINK);
  const { experience } = useSelector(currentExperienceSelector);
  const isUserCollection = experience?.userCollection;

  const handleChange = async (e, value) => {
    setSelectedTab(value);
  };

  useEffect(() => {
    if (element.id) {
      setSelectedTab(element.type);
    }
  }, [element.id]);

  const handleElementUpdate = async (element, values, done) => {
    try {
      if (tabStatus === TAB_STATUSES.HOMEBASE) {
        await ContentElementService.updateContentElement(propertyId, element.mediaItemId, element.id, values);
      }

      if (tabStatus === TAB_STATUSES.CUSTOMIZE) {
        await ElementService.updateElement(propertyId, element.experienceId, element.id, values);
      }

      toast.success(`${element.title} updated successfully`, {
        containerId: "dark",
      });

      setTimeout(() => dispatch(resetCurrentElement()), 300);
    } catch {
      toast.error("Oops! Something went wrong...", {
        containerId: "dark",
      });
    } finally {
      if (done) {
        done();
      }
    }
  };

  const handleElementRemove = async (element) => {
    if (propertyId && element.id) {
      try {
        if (element.scope === ELEMENT_SCOPE.CONTENT && propertyId && element.mediaItemId) {
          await deleteContentElement(propertyId, element.mediaItemId, element.id);
        } else if (propertyId && experienceId) {
          await deleteElement(propertyId, element.experienceId, element.id);
        }

        toast.success(`${element.title} deleted successfully`, {
          containerId: "dark",
        });

        dispatch(resetCurrentElement());
      } catch {
        toast.error("Oops! Something went wrong...", {
          containerId: "dark",
        });
      }
    }
  };

  const renderPanel = () => {
    switch (selectedTab) {
      case ELEMENT_TYPE.LINK:
        return <LinkPanel tabStatus={tabStatus} onRemove={handleElementRemove} onUpdate={handleElementUpdate} />;
      case ELEMENT_TYPE.MAP:
        return <MapPanel />;
      case ELEMENT_TYPE.VOICE_MEMO:
        return <VoiceMemoPanel onRemove={handleElementRemove} onUpdate={handleElementUpdate} />;
      default:
        return "";
    }
  };

  const hasTierAccess = canAccess({
    allowedPermissions: ["elements:create"],
    allowedTiers: tierAccessFlag ? [PROPERTY_TIERS.PRO] : [],
  });

  const hasTierAccessUserCollection = canAccess({
    allowedPermissions: ["elements:create"],
    allowedTiers: [PROPERTY_TIERS.PRO],
  });

  const isTabShown = tabStatus !== TAB_STATUSES.HOMEBASE || hasTierAccess;

  // Voice memo for User collection available only for PRO tier
  const isVoiceMemoTabShown = flags["user-collection"] && isUserCollection ? hasTierAccessUserCollection : isTabShown;

  return (
    <div className={classes.topTabSection}>
      <Tabs
        className={classes.tabsRoot}
        value={selectedTab}
        TabIndicatorProps={{
          style: {
            backgroundColor: "#71B6BC",
            bottom: "1px",
            height: "1px",
          },
        }}
        onChange={handleChange}
        aria-label="element tabs"
        variant="standard"
      >
        <Tab
          className={classes.tab}
          label={ELEMENT_TAB_NAME.LINK}
          value={ELEMENT_TYPE.LINK}
          icon={<ShareCopyIcon className={`${classes.icon} ${classes.link}`} htmlColor="#757575" />}
          iconPosition="start"
          onClick={() => dispatch(resetCurrentElement())}
          data-analytics="element-modal-tab-link"
        />
        {!!flags["new-map-element-view"] && isTabShown && (
          <Tab
            className={classes.tab}
            label={ELEMENT_TAB_NAME.MAP}
            value={ELEMENT_TYPE.MAP}
            icon={<MapElementIcon className={`${classes.icon} ${classes.map}`} />}
            iconPosition="start"
            onClick={() => dispatch(resetCurrentElement())}
            data-analytics="element-modal-tab-map"
            data-testid="map-element"
          />
        )}
        {isVoiceMemoTabShown && (
          <Tab
            className={classes.tab}
            label={ELEMENT_TAB_NAME.VOICE_MEMO}
            value={ELEMENT_TYPE.VOICE_MEMO}
            icon={<VoiceIcon className={`${classes.icon} ${classes.voiceMemo}`} />}
            iconPosition="start"
            onClick={() => dispatch(resetCurrentElement())}
            data-analytics="element-modal-tab-voice"
            data-testid="voice-element"
          />
        )}
      </Tabs>
      {renderPanel()}
    </div>
  );
};

export default ElementsTabs;
